import { css, html, TemplateResult } from 'lit';
import { generateBindings } from '../builder.js';
import { slot } from '../factory.js';
import { useRef, useSlot } from '../hooks/index.js';
import { cssProp, useEffect, useState, view } from '../index.js';
import { HSTack } from '../layout/index.js';
import { Link } from '../router/link.js';
import { Slot } from '../slot.js';
import { spread } from '../utils/spread.js';
import { createView, View } from '../view.js';
import { Button } from './button.js';

export class CounterProps {
  title?: string = '';
  active?: boolean = false;

  @slot('increment')
  incrementTrigger?: View = undefined;

  @slot('decrement')
  decrementTrigger?: View = undefined;

  @slot()
  comment: View[] = [];

  @cssProp('--jsview-counter-text-color')
  textColor?: string = undefined;
}

function template({ title, active }: CounterProps): TemplateResult {
  const [count, setCount] = useState(5);

  const minusButton = useRef();
  const plusButton = useRef();

  useEffect(() => {
    document.title = `You clicked ${count} times`;
  });

  useSlot(
    component => {
      if (component) {
        const listener = () => setCount(prev => prev + 2);
        component.addEventListener('click', listener);
        return () => component.removeEventListener('click', listener);
      }
    },
    [],
    'increment'
  );

  useSlot(
    component => {
      if (component) {
        const listener = () => setCount(prev => prev - 2);
        component.addEventListener('click', listener);
        return () => component.removeEventListener('click', listener);
      }
    },
    [],
    'decrement'
  );

  return html`
    <h2>${title} Nr. ${count}!</h2>
    <div>active: ${active}</div>

    ${HSTack(
      Button('-')
        .onClick(() => setCount(prevCount => prevCount - 1))
        .ref(minusButton),

      Button('+')
        .onClick(() => setCount(prevCount => prevCount + 1))
        .ref(plusButton)
    ).body}
    ${HSTack(Slot('decrement'), Slot(), Slot('increment')).justifyItems(
      'center'
    ).body}
    ${HSTack(
      Link(html`<a href="/">Home</a>`).to('/'),

      Link(html`<a href="/view1">view 1</a>`).to('/view1'),

      Link(html`<a href="/view2">view 2</a>`).to('/view2'),

      createView(html`<input />`),

      createView(({ events }) => {
        const bindings = generateBindings(events);
        return html` <div ${spread(bindings)}>Click me</div>`;
      })
    ).body}
  `;
}

const cssTemplate = css`
  :host {
    display: block;
    padding: 25px;
    color: var(--jsview-counter-text-color, #000);
  }
`;

export const [Counter] = view('jsview-counter', {
  template,
  cssTemplate,
  Props: CounterProps,
  mapBuilder: CounterBuilder => (title = 'Hey there') =>
    CounterBuilder().title(title),
});
